import React from 'react';
import { Route } from 'react-router-dom';

import { Loader } from 'components/Loader/Loader';
import { usePatient } from 'hooks/usePatient/usePatient';
import { AuthLayout } from 'layouts/AuthLayout';
import { ErrorCatcher } from 'layouts/errorCatcher/ErrorCatcher';
import { MainColoredLayout } from 'layouts/MainLayout/MainColoredLayout';
import { MainLayout } from 'layouts/MainLayout/MainLayout';
import { MainMessageLayout } from 'layouts/MainLayout/MainMessageLayout';
import Plain from 'layouts/Plain';
import { ConnectedSimpleLayout as SimpleLayout } from 'layouts/SimpleLayout/SimpleLayout';
import { SoleraLayout } from 'layouts/SoleraLayout/SoleraLayout';
import SSOLoginLayout from 'layouts/SSOLoginLayout';
import { AccessAccount } from 'screens/AccessAccount/AccessAccount';
import { ConfirmationMembershipScreen } from 'screens/ActivateMembership/ConfirmMembership/ConfirmationMembershipScreen';
import { ActivateMembershipScreen } from 'screens/ActivateMembership/EnterAccessCode/ActivateMembershipScreen';
import UniqueIdScreen from 'screens/ActivateMembership/EnterUniqueId/UniqueIdScreen';
import { MembershipActivationSuccess } from 'screens/ActivateMembership/MembershipActivationSuccess/MembershipActivationSuccess';
import { VerifyEligibilityScreen } from 'screens/ActivateMembership/VerifyEligibility/VerifyEligibilityScreen';
import { WalmartConfirmYourIdentityScreen } from 'screens/ActivateMembership/WalmartConfirmYourIdentityScreen';
import { WalmartWinOrBidScreen } from 'screens/ActivateMembership/WalmartWinOrBidScreen';
import { ConfirmAppointment } from 'screens/AppointmentConfirmation/ConfirmAppointment';
import { Appointments } from 'screens/Appointments/Appointments';
import { Appointments as AppointmentsV2 } from 'screens/AppointmentsV2/Appointments';
import { AppointmentVirtualMeeting } from 'screens/AppointmentVirtualMeeting/AppointmentVirtualMeeting';
import { Book } from 'screens/Book/Book';
import { BookingQuiz } from 'screens/BookingQuiz/BookingQuiz';
import { ConfirmNewEmail } from 'screens/ConfirmNewEmail/ConfirmNewEmail';
import { CreateAccountScreen } from 'screens/CreateAccount/CreateAccount';
import { CyclePlanAcknowledge } from 'screens/CyclePlanAcknowledge/CyclePlanAcknowledge';
import { Dashboard } from 'screens/Dashboard/Dashboard';
import DirectPayment from 'screens/DirectPayment/DirectPayment';
import { Documents } from 'screens/Documents/Documents';
import { EmailChange } from 'screens/EmailChange/EmailChange';
import { EmailVerificationScreen } from 'screens/EmailVerificationScreen/EmailVerificationScreen';
import { ForgotPassword } from 'screens/ForgotPassword/ForgotPassword';
import { InformedConsent } from 'screens/InformedConsent/InformedConsent';
import { IntakeForm } from 'screens/IntakeForm/IntakeForm';
import { InvoiceDetails } from 'screens/InvoiceDetails/InvoiceDetails';
import { Invoices } from 'screens/Invoices/Invoices';
import { Kindbody360Index } from 'screens/Kb360/Kindbody360Index';
import { LoginScreen } from 'screens/Login/LoginScreen';
import { LoginSsoAzureMedtronic } from 'screens/LoginSsoAzureMedtronic/LoginSsoAzureMedtronic';
import { Logout } from 'screens/Logout/Logout';
import { Maintenance } from 'screens/Maintenance/Maintenance';
import MedicationInstruction from 'screens/MedicationInstructions/MedicationInstruction';
import MedicationInstructions from 'screens/MedicationInstructions/MedicationInstructions';
import { Messages } from 'screens/Messages/Messages';
import MyJourney from 'screens/MyJourney/MyJourney';
import { MyProfile } from 'screens/MyProfile/MyProfile';
// Layouts
import { NotFound } from 'screens/NotFound/NotFound';
import { Resources } from 'screens/Resources/Resources';
import { Results } from 'screens/Results/Results';
import { Results as ResultsV2 } from 'screens/ResultsV2/Results';
import { SoleraLinkAccount } from 'screens/Solera/SoleraLinkAccount';
import { TreatmentOutcomeSurvey } from 'screens/TreatmentOutcomeSurvey/TreatmentOutcomeSurvey';
import { pageUrl, RoutesPath } from 'utilities/pageUrl';

import RedirectToEnterpriseMembership from './RedirectToEnterpriseMembership';
import { RedirectWithSearchParams } from './RedirectWithSearchParams';
import { withProtectedRoute } from './withProtectedRoute';

export const Redirects = [
  {
    from: pageUrl.signUp(),
    to: pageUrl.createAccount()
  },
  {
    from: RoutesPath.CARE_TEAM,
    to: pageUrl.myProfile('care-team')
  }
].map(({ from, to }) => (
  <Route key={from} path={from} element={<RedirectWithSearchParams to={to} />} />
));

export const Routes = [
  {
    path: RoutesPath.DASHBOARD,
    Component: withProtectedRoute(Dashboard),
    Layout: MainColoredLayout
  },
  {
    path: pageUrl.createAccount(),
    Component: CreateAccountScreen,
    Layout: AuthLayout
  },
  {
    path: RoutesPath.APPOINTMENTS,
    Component: withProtectedRoute(AppointmentsV2),
    Layout: MainColoredLayout
  },
  {
    path: RoutesPath.DEPRECATED_APPOINTMENTS,
    Component: withProtectedRoute(Appointments),
    Layout: MainLayout
  },
  {
    path: RoutesPath.BOOKING_BOOK,
    Component: withProtectedRoute(Book),
    Layout: MainLayout
  },
  {
    path: RoutesPath.BOOKING_QUIZ,
    Component: BookingQuiz,
    Layout: SimpleLayout
  },
  {
    path: RoutesPath.ENTER_MEMBERSHIP_ACCESS_CODE,
    Component: withProtectedRoute(ActivateMembershipScreen),
    Layout: MainLayout
  },
  {
    path: RoutesPath.MEMBERSHIP_ACTIVATION_SUCCESS,
    Component: withProtectedRoute(MembershipActivationSuccess),
    Layout: MainLayout
  },
  {
    path: RoutesPath.VERIFY_MEMBERSHIP_ELIGIBILITY,
    Component: withProtectedRoute(VerifyEligibilityScreen),
    Layout: MainLayout
  },
  {
    path: RoutesPath.ENTER_MEMBERSHIP_UNIQUE_ID,
    Component: withProtectedRoute(UniqueIdScreen),
    Layout: MainLayout
  },
  {
    path: RoutesPath.ACTIVATE_MEMBERSHIP_WALMART_WIN_BID,
    Component: withProtectedRoute(WalmartWinOrBidScreen),
    Layout: MainLayout
  },
  {
    path: RoutesPath.ACTIVATE_MEMBERSHIP_WALMART_CONFIRM_IDENTITY,
    Component: withProtectedRoute(WalmartConfirmYourIdentityScreen),
    Layout: MainLayout
  },
  {
    path: RoutesPath.CONFIRM_MEMBERSHIP_ACTIVATION,
    Component: withProtectedRoute(ConfirmationMembershipScreen),
    Layout: MainLayout
  },
  {
    path: RoutesPath.RESULTS,
    Component: withProtectedRoute(Results),
    Layout: MainLayout
  },
  {
    path: RoutesPath.RESULTS_V2,
    Component: withProtectedRoute(ResultsV2),
    Layout: MainColoredLayout
  },
  // TODO Remove when all links (emails, outside of this app, etc.) have been migrated
  {
    path: RoutesPath.MY_JOURNEY,
    Component: withProtectedRoute(MyJourney),
    Layout: MainLayout
  },
  {
    path: RoutesPath.DOCUMENTS,
    Component: withProtectedRoute(Documents),
    Layout: MainColoredLayout
  },
  {
    path: RoutesPath.LOGIN,
    Component: LoginScreen,
    Layout: SimpleLayout
  },
  {
    path: pageUrl.loginSsoMedtronic(),
    Component: LoginSsoAzureMedtronic,
    Layout: SSOLoginLayout
  },
  {
    path: RoutesPath.LOGOUT,
    Component: Logout,
    Layout: SimpleLayout
  },
  {
    path: RoutesPath.FORGOT_PASSWORD,
    Component: ForgotPassword,
    Layout: SimpleLayout
  },
  {
    path: pageUrl.accessAccount(),
    Component: AccessAccount,
    Layout: SimpleLayout
  },
  {
    path: `${RoutesPath.CONSENTS}/:id`,
    Component: withProtectedRoute(InformedConsent),
    Layout: MainLayout
  },
  {
    path: pageUrl.emailAddressVerification(),
    Component: EmailVerificationScreen,
    Layout: SimpleLayout
  },
  {
    path: pageUrl.medicationInstructions(),
    Component: MedicationInstructions,
    Layout: Plain
  },
  {
    path: pageUrl.medicationInstruction(),
    Component: MedicationInstruction,
    Layout: Plain
  },
  {
    path: RoutesPath.MY_PROFILE,
    Component: withProtectedRoute(MyProfile),
    Layout: MainLayout
  },
  {
    path: pageUrl.activateKindbodyBenefit(),
    Component: RedirectToEnterpriseMembership,
    Layout: MainLayout
  },
  {
    path: pageUrl.kindbodyBenefit(),
    Component: RedirectToEnterpriseMembership,
    Layout: MainLayout
  },
  {
    path: RoutesPath.INVOICES,
    Component: withProtectedRoute(Invoices),
    Layout: MainColoredLayout
  },
  {
    path: `${RoutesPath.INVOICES}/:id`,
    Component: withProtectedRoute(InvoiceDetails),
    Layout: MainColoredLayout
  },
  {
    path: RoutesPath.RESOURCES,
    Component: withProtectedRoute(Resources),
    Layout: MainColoredLayout
  },
  {
    path: RoutesPath.KINDBODY_360,
    Component: withProtectedRoute(Kindbody360Index),
    Layout: MainLayout
  },
  {
    path: pageUrl.cyclePlanAcknowledge(),
    Component: withProtectedRoute(CyclePlanAcknowledge),
    Layout: MainLayout
  },
  {
    path: pageUrl.intakeForm(),
    Component: withProtectedRoute(IntakeForm),
    Layout: Plain
  },
  {
    path: pageUrl.treatmentOutcomeSurvey(),
    Component: withProtectedRoute(TreatmentOutcomeSurvey),
    Layout: Plain
  },
  {
    path: pageUrl.viosPayment(),
    Component: DirectPayment,
    Layout: SimpleLayout
  },
  {
    path: pageUrl.viosPayments(),
    Component: DirectPayment,
    Layout: SimpleLayout
  },
  {
    path: pageUrl.appointmentVirtualMeeting(),
    Component: AppointmentVirtualMeeting,
    Layout: SimpleLayout
  },
  {
    path: pageUrl.changeEmail(),
    Component: withProtectedRoute(EmailChange),
    Layout: SimpleLayout
  },
  {
    path: pageUrl.confirmNewEmail(),
    Component: ConfirmNewEmail,
    Layout: SimpleLayout
  },
  {
    path: pageUrl.messages(),
    Component: withProtectedRoute(Messages),
    Layout: MainMessageLayout
  },
  {
    path: `${pageUrl.messages()}/:categoryName/:conversationId`,
    Component: withProtectedRoute(Messages),
    Layout: MainMessageLayout
  },
  {
    path: `${pageUrl.messages()}/:categoryName`,
    Component: withProtectedRoute(Messages),
    Layout: MainMessageLayout
  },
  {
    path: RoutesPath.SOLERA_LANDING,
    Component: SoleraLinkAccount,
    Layout: SoleraLayout
  },
  {
    path: RoutesPath.APPOINTMENT_CONFIRMATION,
    Component: ConfirmAppointment,
    Layout: MainLayout
  },
  {
    path: RoutesPath.MAINTENANCE,
    Component: Maintenance,
    Layout: SimpleLayout
  }
];

export const getNotFoundElement = () => {
  const { isLoggedIn, loading } = usePatient(true);

  if (loading) return <Loader absoluteCentered />;

  return (
    <SimpleLayout isLoggedIn={isLoggedIn} isNotFoundPage={true}>
      <ErrorCatcher id="route">
        <NotFound />
      </ErrorCatcher>
    </SimpleLayout>
  );
};
